// src/components/AdminDashboard.js
// Admin-only bento-grid KPI dashboard. Single source of truth: the
// get_admin_dashboard_kpis(p_tenant_id) RPC. No ad-hoc table queries here.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';

// ── Palette ──────────────────────────────────────────────────────────────────
const COLOR = {
  primary: '#d8262c',
  text:    '#1a1a1a',
  card:    '#f5f5f5',
  border:  '#eaeaea',
  up:      '#22c55e',
  down:    '#ef4444',
  gray:    '#9ca3af',
};

// ── Utility helpers (module scope) ──────────────────────────────────────────
function formatUSD(n) {
  const num = Number(n) || 0;
  return num.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDeltaPct(curr, prev) {
  const c = Number(curr) || 0;
  const p = Number(prev) || 0;
  if (p === 0 && c > 0) return { pct: 100, symbol: '▲', color: COLOR.up };
  if (p === 0 && c === 0) return { pct: 0, symbol: '—', color: COLOR.gray };
  const pct = ((c - p) / p) * 100;
  if (pct > 0) return { pct, symbol: '▲', color: COLOR.up };
  if (pct < 0) return { pct, symbol: '▼', color: COLOR.down };
  return { pct: 0, symbol: '—', color: COLOR.gray };
}

function formatHours(h, sample = 0) {
  const hours = Number(h) || 0;
  if (hours === 0 && sample === 0) return '—';
  const totalMinutes = Math.round(hours * 60);
  const hh = Math.floor(totalMinutes / 60);
  const mm = totalMinutes % 60;
  return `${hh}h ${mm}m`;
}

// ── Delta pill (presentational, module scope) ───────────────────────────────
const DeltaPill = ({ curr, prev }) => {
  const { pct, symbol, color } = formatDeltaPct(curr, prev);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: '3px',
      fontSize: '12px', fontWeight: 700, color,
    }}>
      {symbol} {symbol === '—' ? '' : `${Math.abs(pct).toFixed(1)}%`}
    </span>
  );
};

// ── Hand-rolled SVG donut ring ───────────────────────────────────────────────
const Donut = ({ paid, unpaid, centerLabel }) => {
  const total = (Number(paid) || 0) + (Number(unpaid) || 0);
  const r = 45;
  const circumference = 2 * Math.PI * r;
  const fraction = total > 0 ? (Number(paid) || 0) / total : 0;
  const offset = circumference - fraction * circumference;

  return (
    <svg viewBox="0 0 120 120" width="110" height="110">
      <circle cx="60" cy="60" r={r} fill="none" stroke={COLOR.border} strokeWidth="12" />
      {total > 0 && (
        <circle
          cx="60" cy="60" r={r} fill="none"
          stroke={COLOR.up} strokeWidth="12"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 60 60)"
        />
      )}
      <text x="60" y="56" textAnchor="middle" fontSize={total > 0 ? '18' : '11'} fontWeight="700" fill={COLOR.text}>
        {total > 0 ? `${Math.round(fraction * 100)}%` : 'Sin facturas'}
      </text>
      {total > 0 && (
        <text x="60" y="72" textAnchor="middle" fontSize="9" fill={COLOR.gray}>
          pagado
        </text>
      )}
    </svg>
  );
};

// ── Cell shell ────────────────────────────────────────────────────────────────
const Cell = ({ span, minimal, children, style }) => (
  <div
    className="adb-cell"
    style={{
      gridColumn: `span ${span}`,
      background: COLOR.card,
      border: `1px solid ${COLOR.border}`,
      borderRadius: '14px',
      padding: minimal ? '14px 16px' : '18px 20px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      minWidth: 0,
      ...style,
    }}
  >
    {children}
  </div>
);

const Label = ({ children }) => (
  <div style={{ fontSize: '12px', fontWeight: 600, color: COLOR.gray, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
    {children}
  </div>
);

const BigNumber = ({ children }) => (
  <div style={{ fontSize: '26px', fontWeight: 800, color: COLOR.text, lineHeight: 1.15 }}>
    {children}
  </div>
);

// ── Cell customization registry ─────────────────────────────────────────────
const CELL_REGISTRY = [
  { key: 'F1', label: 'Ingresos — Semana' },
  { key: 'F2', label: 'Ingresos — Mes' },
  { key: 'F3', label: 'Pagado vs. Pendiente' },
  { key: 'V1', label: 'Casillero — Semana' },
  { key: 'V2', label: 'Encomienda — Semana' },
  { key: 'V3', label: 'Volumen — Mes' },
  { key: 'M1', label: 'Manifiesto Abierto' },
  { key: 'I1', label: 'Tiempo de Recepción' },
];
const STORAGE_KEY = 'co-dashboard-cells-v1';
const DEFAULT_VISIBLE_CELLS = { F1: true, F2: true, F3: true, V1: true, V2: true, V3: true, M1: true, I1: true };

const AdminDashboard = ({ tenant }) => {
  const [status, setStatus] = useState('loading'); // 'loading' | 'error' | 'data'
  const [data, setData] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const [donutMode, setDonutMode] = useState('month'); // 'month' | 'manifest'
  const [refreshing, setRefreshing] = useState(false);

  const [visibleCells, setVisibleCells] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? { ...DEFAULT_VISIBLE_CELLS, ...JSON.parse(stored) } : DEFAULT_VISIBLE_CELLS;
    } catch {
      return DEFAULT_VISIBLE_CELLS;
    }
  });
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  /* eslint-disable */
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(visibleCells));
    } catch (err) {
      console.error('[AdminDashboard] failed to persist cell visibility:', err);
    }
  }, [visibleCells]);
  /* eslint-enable */

  /* eslint-disable */
  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);
  /* eslint-enable */

  const toggleCell = (key) => {
    setVisibleCells((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  /* eslint-disable */
  const fetchKpis = useCallback(async (isRefresh) => {
    if (!tenant?.id) return;
    if (isRefresh) setRefreshing(true);
    else setStatus('loading');
    try {
      const { data: result, error } = await supabase.rpc('get_admin_dashboard_kpis', {
        p_tenant_id: tenant.id,
      });
      if (error) throw error;
      setData(result);
      setStatus('data');
    } catch (err) {
      console.error('[AdminDashboard] fetchKpis failed:', err);
      setErrorMsg(err.message || 'Error al cargar el panel.');
      setStatus('error');
    } finally {
      if (isRefresh) setRefreshing(false);
    }
  }, [tenant]);
  /* eslint-enable */

  useEffect(() => {
    fetchKpis(false);
  }, [fetchKpis]);

  if (status === 'loading') {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '240px', color: COLOR.gray, fontWeight: 600 }}>
        Cargando panel...
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '240px', gap: '10px' }}>
        <div style={{ color: COLOR.down, fontWeight: 700 }}>No se pudo cargar el panel.</div>
        <div style={{ color: COLOR.gray, fontSize: '13px' }}>{errorMsg}</div>
        <button
          onClick={() => fetchKpis(false)}
          style={{ background: COLOR.primary, color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 16px', fontWeight: 700, cursor: 'pointer' }}
        >
          Reintentar
        </button>
      </div>
    );
  }

  // status === 'data'
  const { financials, volume, intake, manifest, generated_at } = data;
  const manifestOpen = !!manifest.manifest_id;
  const donutPaid = donutMode === 'month' ? financials.paid_month : financials.paid_open_manifest;
  const donutUnpaid = donutMode === 'month' ? financials.unpaid_month : financials.unpaid_open_manifest;

  const generatedLocal = generated_at
    ? new Date(generated_at).toLocaleString('es-SV', { timeZone: 'America/El_Salvador', dateStyle: 'short', timeStyle: 'medium' })
    : '—';

  return (
    <div className="adb-wrapper" style={{ position: 'relative' }}>
      <style>{`
        .adb-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
        }
        @media (max-width: 640px) {
          .adb-grid { grid-template-columns: 1fr; }
          .adb-cell { grid-column: span 1 !important; }
        }
      `}</style>

      <span
        onClick={() => setMenuOpen((o) => !o)}
        style={{ position: 'absolute', top: '8px', right: '8px', fontSize: '20px', cursor: 'pointer', zIndex: 2 }}
        title="Personalizar panel"
      >
        ⚙
      </span>

      {menuOpen && (
        <div
          ref={menuRef}
          style={{
            position: 'absolute', top: '36px', right: '8px', zIndex: 3,
            background: '#fff', border: `1px solid ${COLOR.border}`, borderRadius: '8px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)', padding: '12px', minWidth: '240px',
            maxWidth: 'calc(100vw - 32px)',
          }}
        >
          <div style={{ fontSize: '14px', fontWeight: 600, color: COLOR.text, marginBottom: '4px' }}>
            Personalizar panel
          </div>
          {CELL_REGISTRY.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => toggleCell(key)}
              style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 6px', border: 'none', background: 'transparent',
                cursor: 'pointer', width: '100%', fontFamily: 'inherit', fontSize: '13px', color: COLOR.text,
              }}
            >
              <span>{label}</span>
              {visibleCells[key] && (
                <span style={{ color: COLOR.up, fontSize: '16px', fontWeight: 700 }}>✓</span>
              )}
            </button>
          ))}
        </div>
      )}

      <div className="adb-grid">

        {/* F1 — Weekly revenue */}
        {visibleCells.F1 && (
        <Cell span={1}>
          <Label>Ingresos — Semana</Label>
          <BigNumber>{formatUSD(financials.revenue_week)}</BigNumber>
          <div style={{ marginTop: '6px' }}>
            <DeltaPill curr={financials.revenue_week} prev={financials.revenue_week_prev} />
          </div>
        </Cell>
        )}

        {/* F2 — Monthly revenue */}
        {visibleCells.F2 && (
        <Cell span={1}>
          <Label>Ingresos — Mes</Label>
          <BigNumber>{formatUSD(financials.revenue_month)}</BigNumber>
          <div style={{ marginTop: '6px' }}>
            <DeltaPill curr={financials.revenue_month} prev={financials.revenue_month_prev} />
          </div>
        </Cell>
        )}

        {/* F3 — Paid vs unpaid */}
        {visibleCells.F3 && (
        <Cell span={2}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
            <Label>Pagado vs. Pendiente</Label>
            <div style={{ display: 'flex', gap: '4px', background: '#fff', border: `1px solid ${COLOR.border}`, borderRadius: '8px', padding: '2px' }}>
              <button
                onClick={() => setDonutMode('month')}
                style={{
                  fontSize: '11px', fontWeight: 700, border: 'none', borderRadius: '6px', padding: '4px 8px', cursor: 'pointer',
                  background: donutMode === 'month' ? COLOR.primary : 'transparent',
                  color: donutMode === 'month' ? '#fff' : COLOR.gray,
                }}
              >
                Este mes
              </button>
              <button
                onClick={() => manifestOpen && setDonutMode('manifest')}
                disabled={!manifestOpen}
                style={{
                  fontSize: '11px', fontWeight: 700, border: 'none', borderRadius: '6px', padding: '4px 8px',
                  cursor: manifestOpen ? 'pointer' : 'not-allowed',
                  background: donutMode === 'manifest' ? COLOR.primary : 'transparent',
                  color: donutMode === 'manifest' ? '#fff' : COLOR.gray,
                  opacity: manifestOpen ? 1 : 0.5,
                }}
              >
                Manifiesto abierto
              </button>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginTop: '10px', flexWrap: 'wrap' }}>
            <Donut paid={donutPaid} unpaid={donutUnpaid} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '13px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: COLOR.up, display: 'inline-block' }} />
                Pagado: <strong>{formatUSD(donutPaid)}</strong>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: COLOR.border, display: 'inline-block' }} />
                Pendiente: <strong>{formatUSD(donutUnpaid)}</strong>
              </div>
            </div>
          </div>
        </Cell>
        )}

        {/* V1 — Casillero this week */}
        {visibleCells.V1 && (
        <Cell span={1}>
          <Label>Casillero — Semana</Label>
          <BigNumber>{volume.casillero_week}</BigNumber>
          <div style={{ marginTop: '6px' }}>
            <DeltaPill curr={volume.casillero_week} prev={volume.casillero_week_prev} />
          </div>
        </Cell>
        )}

        {/* V2 — Encomienda this week */}
        {visibleCells.V2 && (
        <Cell span={1}>
          <Label>Encomienda — Semana</Label>
          <BigNumber>{volume.encomienda_week}</BigNumber>
          <div style={{ marginTop: '6px' }}>
            <DeltaPill curr={volume.encomienda_week} prev={volume.encomienda_week_prev} />
          </div>
        </Cell>
        )}

        {/* V3 — Monthly volume */}
        {visibleCells.V3 && (
        <Cell span={2}>
          <Label>Volumen — Mes</Label>
          <div style={{ display: 'flex', gap: '32px', marginTop: '4px' }}>
            <div>
              <div style={{ fontSize: '11px', color: COLOR.gray, fontWeight: 600 }}>Casillero</div>
              <div style={{ fontSize: '24px', fontWeight: 800, color: COLOR.text }}>{volume.casillero_month}</div>
            </div>
            <div>
              <div style={{ fontSize: '11px', color: COLOR.gray, fontWeight: 600 }}>Encomienda</div>
              <div style={{ fontSize: '24px', fontWeight: 800, color: COLOR.text }}>{volume.encomienda_month}</div>
            </div>
          </div>
        </Cell>
        )}

        {/* M1 — Manifest status */}
        {visibleCells.M1 && (
        <Cell span={2}>
          {manifestOpen ? (
            <>
              <Label>Manifiesto {manifest.manifest_id}</Label>
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginTop: '2px' }}>
                <div>
                  <div style={{ fontSize: '11px', color: COLOR.gray }}>Total</div>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: COLOR.text }}>{manifest.total_packages}</div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: COLOR.gray }}>Casillero</div>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: COLOR.text }}>{manifest.casillero_count}</div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: COLOR.gray }}>Encomienda</div>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: COLOR.text }}>{manifest.encomienda_count}</div>
                </div>
                <div>
                  <div style={{ fontSize: '11px', color: COLOR.gray }}>Cajas</div>
                  <div style={{ fontSize: '18px', fontWeight: 700, color: COLOR.text }}>{manifest.box_count}</div>
                </div>
              </div>

              <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: `1px dashed ${COLOR.border}` }}>
                {manifest.unboxed_count > 5 ? (
                  <div>
                    <div style={{ fontSize: '22px', fontWeight: 800, color: COLOR.primary }}>{manifest.unboxed_count}</div>
                    <div style={{ fontSize: '11px', color: COLOR.gray }}>paquetes sin caja asignada</div>
                  </div>
                ) : manifest.unboxed_count > 0 ? (
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    {(manifest.unboxed_sample || []).map((pkg, i) => (
                      <span key={i} style={{
                        fontSize: '11px', fontWeight: 600, color: COLOR.text,
                        background: '#fff', border: `1px solid ${COLOR.border}`,
                        borderRadius: '99px', padding: '3px 10px',
                      }}>
                        {pkg.display_id} · {pkg.customer_id} · {pkg.pkg_type}
                      </span>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: '12px', color: COLOR.gray }}>Todos los paquetes están en caja.</div>
                )}
              </div>
            </>
          ) : (
            <div style={{ color: COLOR.gray, fontWeight: 600 }}>Ningún manifiesto abierto</div>
          )}
        </Cell>
        )}

        {/* I1 — Intake time (minimal) */}
        {visibleCells.I1 && (
        <Cell span={1} minimal>
          <Label>Tiempo de Recepción</Label>
          <BigNumber>{formatHours(intake.avg_hours_week, intake.sample_week)}</BigNumber>
          <div style={{ fontSize: '11px', color: COLOR.gray, marginTop: '4px' }}>
            Muestra: {intake.sample_week} encomiendas
          </div>
        </Cell>
        )}

        {/* M2 — Refresh (always visible — not part of the customization registry) */}
        <Cell span={1} style={{ alignItems: 'center', textAlign: 'center' }}>
          <button
            onClick={() => fetchKpis(true)}
            disabled={refreshing}
            style={{
              background: COLOR.primary, color: '#fff', border: 'none', borderRadius: '10px',
              padding: '10px 18px', fontWeight: 700, cursor: refreshing ? 'default' : 'pointer',
              opacity: refreshing ? 0.6 : 1,
            }}
          >
            {refreshing ? 'Actualizando...' : '↻ Actualizar'}
          </button>
          <div style={{ fontSize: '10px', color: COLOR.gray, marginTop: '8px' }}>
            {generatedLocal}
          </div>
        </Cell>

      </div>
    </div>
  );
};

export default AdminDashboard;
